import React, {ReactNode} from 'react'
import {Link as RouteLink} from 'react-router-dom'
import {Box, Breadcrumb as ChakraBreadcrumb, BreadcrumbItem, BreadcrumbLink, Skeleton, useMultiStyleConfig} from '@chakra-ui/react'
import {useBreadcrumbRedirects} from '_customHooks'

interface BreadcrumbCategory {
    id: string
    htmlValue: string | ReactNode
    url?: string
    navigable?: boolean
}

interface BreadcrumbProps {
    items?: Array<BreadcrumbCategory>
    variant?: 'default' | 'pdp'
}

/**
 * @name Breadcrumb
 *
 * @component
 *
 * @description
 * Renders a breadcrumb navigation using Chakra UI components.
 *
 * @example
 * ```typescript
 * const items = [
 *   { id: '1', url: '/home', htmlValue: 'Home' },
 *   { id: '2', url: '/about', htmlValue: 'About' },
 *   { id: '3', url: '/contact', htmlValue: 'Contact' }
 * ]
 * <Breadcrumb items={items} />
 * ```
 *
 * @param {BreadcrumbProps} props - The props for the Breadcrumb component.
 * @param {Array<BreadcrumbCategory>} [props.items] - An array of breadcrumb items.
 * @param {string} props.items[].id - The unique identifier for each breadcrumb item.
 * @param {string} props.items[].url - The URL to navigate to when the breadcrumb item is clicked.
 * @param {string} props.items[].htmlValue - The display name of the breadcrumb item.
 *
 * @returns {JSX.Element} - Rendered Breadcrumb component.
 */
const Breadcrumb = ({items, variant = 'default'}: BreadcrumbProps): JSX.Element => {
    const styles = useMultiStyleConfig('Breadcrumb', {variant}) as Record<string, Record<string, string>>

    // Pre-resolve breadcrumb redirects in the background (idle) so a click on an ancestor that
    // the backend redirects goes straight to the final page, without an intermediate landing.
    const resolvedItems = useBreadcrumbRedirects(items as Parameters<typeof useBreadcrumbRedirects>[0])

    return (
        <ChakraBreadcrumb
            {...styles.container}
            separator={
                <Box {...styles.separator} aria-hidden="true">
                    {'/'}
                </Box>
            }
        >
            {resolvedItems?.map((item, index) => {
                const isCurrent = index === resolvedItems.length - 1
                const redirect = item.redirect

                // Use `sx` (not prop spread) so nested selectors like `&[aria-current="page"]`
                // are treated as CSS by Emotion instead of being forwarded as DOM attributes,
                // which triggered a "Invalid attribute name" React warning.
                let linkNode: ReactNode
                if (!item.url || item.url.length === 0) {
                    linkNode = (
                        <BreadcrumbLink sx={styles.link} isCurrentPage>
                            {item.htmlValue}
                        </BreadcrumbLink>
                    )
                } else if (redirect?.shouldRedirect && redirect.mode === 'hard-navigation') {
                    // Destination served outside the PWA (e.g. SFRA): plain anchor to the absolute
                    // URL so the browser reaches the correct public domain on click.
                    linkNode = (
                        <BreadcrumbLink sx={styles.link} href={redirect.absoluteUrl ?? redirect.target}>
                            {item.htmlValue}
                        </BreadcrumbLink>
                    )
                } else {
                    // PWA destination: route to the resolved target when available, otherwise to the
                    // original url (which lands and, if it redirects, bounces via useCustomSdk).
                    const to = redirect?.shouldRedirect && redirect.target ? redirect.target : item.url
                    linkNode = (
                        <BreadcrumbLink sx={styles.link} as={RouteLink} to={to}>
                            {item.htmlValue}
                        </BreadcrumbLink>
                    )
                }

                return (
                    <BreadcrumbItem sx={styles.item} key={`${item.id}-${index}`} isCurrentPage={isCurrent}>
                        {linkNode}
                    </BreadcrumbItem>
                )
            })}
        </ChakraBreadcrumb>
    )
}

/**
 * @name BreadcrumbSkeleton
 *
 * @component
 *
 * @description
 * Renders skeleton breadcrumb for lazy loading.
 *
 * @returns {JSX.Element} - Rendered BreadcrumbSkeleton component.
 */
const BreadcrumbSkeleton = (): JSX.Element => {
    return (
        <ChakraBreadcrumb data-testid="breadcrumb-skeleton">
            <BreadcrumbItem>
                <Skeleton height="20px" width="100px" />
            </BreadcrumbItem>
            <BreadcrumbItem>
                <Skeleton height="20px" width="100px" />
            </BreadcrumbItem>
            <BreadcrumbItem>
                <Skeleton height="20px" width="100px" />
            </BreadcrumbItem>
        </ChakraBreadcrumb>
    )
}

export {Breadcrumb, BreadcrumbSkeleton}
